import { computed, inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { BpSdkStoreService } from '../../services/bp-sdk-store-service/bp-sdk-store.service';
import {
  selectEnrollmentsHaveBeenLoaded,
  selectHasAnyOptInEnrollments,
  selectHasAnySubOrPlacementDeskEnrollments,
} from '../../store/enrollments/enrollments.selectors';
import {
  selectComplianceFormHasBeenLoaded,
  selectHasBankAccount,
  selectIsMainComplianceFormComplete,
  selectNeedsBankAccount,
  selectNeedsCompliance,
} from '../../store/compliance-form/compliance-form.selectors';
import { isFeatureEnabled, selectUser } from '@boldpenguin/sdk';
import { PermissionsService } from '../permissions/permissions.service';
import { SetupTasksPermissions } from '../../../permissions/permissions';
import { TenantType } from '../../../features/settings/models/tenant.model';
import { toSignal } from '@angular/core/rxjs-interop';
import { PlanCodes } from '../../../shared/models/plan-codes';
import { GetAllEnrollments } from '../../store/enrollments/enrollments.actions';
import { ComplianceStatuses } from '../../models/compliance-status.model';
import { ProductFeatureType } from '../../../permissions/permissions.model';

@Injectable({
  providedIn: 'root',
})
export class TasksService {
  private readonly store = inject(Store);
  private readonly bpStoreService = inject(BpSdkStoreService);
  private permissionsService = inject(PermissionsService);
  private loadingComplete = computed(
    () =>
      this.store.selectSignal(selectComplianceFormHasBeenLoaded)() &&
      this.store.selectSignal(selectEnrollmentsHaveBeenLoaded)(),
  );
  private useComplianceStatusFeatureFlagEnabled = computed(() => {
    const state = this.bpStoreService.sdkReadyState();
    return (
      state && isFeatureEnabled(state, ProductFeatureType.UseComplianceStatus)
    );
  });

  public readonly requiresCarriers = computed(
    () => !this.store.selectSignal(selectHasAnyOptInEnrollments)(),
  );

  public readonly requiresCompliance = this.store.selectSignal(
    selectNeedsCompliance,
  );

  public readonly hasDocuments = computed(() => {
    const state = this.bpStoreService.sdkReadyState();
    const user = state && selectUser(state);
    const rawInfo = user?.extra?.raw_info;
    const hasEo = rawInfo?.tenant_eo_declaration_url != null;
    const hasW9 = rawInfo?.tenant_w9_submitted;
    return hasEo && hasW9;
  });

  public readonly requiresDocuments = computed(() => {
    if (!this.hasNonDirectAppointments()) {
      return false;
    }

    return !this.hasDocuments();
  });

  public readonly requiresBankAccount = this.store.selectSignal(
    selectNeedsBankAccount,
  );

  public readonly hasBankAccount =
    this.store.selectSignal(selectHasBankAccount);

  public readonly hasCompliance = this.store.selectSignal(
    selectIsMainComplianceFormComplete,
  );

  public readonly hasNonDirectAppointments = this.store.selectSignal(
    selectHasAnySubOrPlacementDeskEnrollments,
  );

  public readonly requiredTasksCount = computed(() => {
    const needsCarriers = this.requiresCarriers();
    const hasSubsOrPlacementsDesk = this.hasNonDirectAppointments();

    if (!needsCarriers) {
      return hasSubsOrPlacementsDesk ? 4 : 1;
    }

    return 1;
  });

  public readonly completedTasksCount = computed(() => {
    const tasks = [
      !this.requiresCarriers(),
      this.hasCompliance(),
      this.hasDocuments(),
      this.hasBankAccount(),
    ];

    if (this.requiresCarriers()) return 0;
    return tasks.filter((v) => !!v).length;
  });

  public readonly isIaPlan = computed(() => {
    const state = this.bpStoreService.sdkReadyState();
    const user = state && selectUser(state);
    const tenant_type = user?.extra?.raw_info?.tenant_type;
    const plan = user?.extra?.raw_info?.plan_sku;
    if (plan === PlanCodes.Enterprise || plan === PlanCodes.Legacy) {
      return false;
    }
    return tenant_type === TenantType.IA;
  });

  public readonly hasTasksPermissions = toSignal(
    this.permissionsService.hasRoutePermission(SetupTasksPermissions),
  );

  public readonly hasRequiredTasks = computed(() => {
    if (this.isIaPlan() && this.loadingComplete()) {
      return this.requiredTasksCount() > this.completedTasksCount();
    }
    return false;
  });

  public readonly hasTasksForSubAppointments = computed(() => {
    if (this.isIaPlan()) {
      return [
        this.hasDocuments(),
        this.hasCompliance(),
        this.hasBankAccount(),
      ].some((v) => !v);
    }
    return false;
  });

  public readonly isComplianceApproved = computed(() => {
    if (this.useComplianceStatusFeatureFlagEnabled()) {
      const state = this.bpStoreService.sdkReadyState();
      const user = state && selectUser(state);
      const rawInfo = user?.extra?.raw_info;
      return rawInfo?.compliance_status === ComplianceStatuses.Approved;
    } else {
      return true;
    }
  });

  public initTasks() {
    this.store.dispatch(GetAllEnrollments());
  }
}
